import { Divider, Flex, Text } from '@chakra-ui/react';
import React from 'react';
import CustomFieldLabel from '../../../components/customfield-label/CustomFieldLabel';
import { ProviderName } from '../../../components/provider-name/ProviderName';
import { Column } from '../../../components/resource-grid/ResourceGrid';
import { Provider } from '../../../types';
import { getCustomFieldValue } from '../../../utils/customFieldUtils';
import { getNewPatientPriorityProps } from '../../../utils/getNewPatientPriorityProps';
import { constructPracticeAddress } from '../../../utils/practiceUtils';
import AthenaDepartmentLink from '../practices/AthenaDepartmentLink';
import ProviderPacStatus from './ProviderPacStatus';
import SupervisingPhysicianBadge from './SupervisingPhysicianBadge';

export const createProviderColumns = (
  duplicateProviders: Record<string, string> = {}
): Column<Provider>[] => [
  // Actions column is now handled by ResourceGrid standardized action menu
  {
    header: '',
    accessor: 'actions' as keyof Provider,
    width: '2.75rem',
    minWidth: '2.75rem',
    isStatic: true,
  },
  {
    header: 'Name',
    accessor: 'firstname',
    isCustomRender: true,
    width: '13.75rem',
    render: (row: Provider) =>
      row.firstname || row.lastname ? (
        <ProviderName
          provider={row}
          duplicateProviders={duplicateProviders}
          mode="table"
          size="sm"
        />
      ) : null,
  },
  {
    header: 'Suffix',
    accessor: 'suffix',
    isCustomRender: true,
    width: '4rem',
    render: (row: Provider) =>
      row.suffix ? (
        <ProviderName
          provider={row}
          mode="table"
          useNavigateTextStyle={true}
          customName={`${row.suffix}`}
        />
      ) : null,
  },
  {
    header: 'Specialty',
    accessor: 'specialty',
    isCustomRender: true,
    width: '12rem',
    render: (row: Provider) =>
      row.specialty ? <Text>{row.specialty}</Text> : null,
  },
  {
    header: 'Address',
    accessor: 'relatedPractices',
    isCustomRender: true,
    width: '20rem',
    render: (row: Provider) => {
      if (!row.relatedPractices?.length) {
        return (
          <Text fontSize="0.75rem" lineHeight="1rem" color="gray.500">
            -
          </Text>
        );
      }

      const addresses = row.relatedPractices
        .map((practice) => {
          // Check if practice has address fields
          if (
            !practice.address1 &&
            !practice.city &&
            !practice.state
          ) {
            return null;
          }
          return constructPracticeAddress(practice);
        })
        .filter(Boolean);

      if (addresses.length === 0) {
        return (
          <Text fontSize="0.75rem" lineHeight="1rem" color="gray.500">
            -
          </Text>
        );
      }

      return (
        <Text fontSize="0.75rem" lineHeight="1rem">
          {addresses.join('; ')}
        </Text>
      );
    },
  },
  // {
  //   header: 'Market',
  //   accessor: 'relatedPractices',
  //   isCustomRender: true,
  //   render: (row: Provider) =>
  //     row.relatedPractices?.length ? (
  //       <Text>
  //         {row.relatedPractices
  //           .map((practice) => practice.market || '')
  //           .filter(Boolean)
  //           .join(', ')}
  //       </Text>
  //     ) : null,
  // },
  {
    header: 'NPI',
    accessor: 'npi',
    width: '8rem',
    render: (row: Provider) =>
      row.npi ? <Text>{row.npi}</Text> : null,
  },
  {
    header: 'Athena Department',
    accessor: 'providerBridge',
    isCustomRender: true,
    width: '16rem',
    render: (row: Provider) => {
      if (!row.providerBridge?.length) {
        return (
          <Text fontSize="0.75rem" lineHeight="1rem" color="gray.500">
            -
          </Text>
        );
      }

      // Get Athena departments from provider bridge metadata and match with practices
      const athenaDepartments = row.providerBridge
        .map((bridge) => {
          const departmentName =
            bridge.metadata?.['Athena-Department'];
          const departmentLink = bridge.metadata?.['Athena-Link'];

          // Find matching practice from relatedPractices
          const practice = row.relatedPractices?.find(
            (p) => p.id === bridge.practiceId
          );

          // Include entry even if department info is missing
          // This ensures we show the practice name regardless
          // Build location string (city, state)
          const location = practice
            ? [practice.city, practice.state]
                .filter(Boolean)
                .join(', ')
            : undefined;

          return {
            departmentName:
              departmentName &&
              departmentName.trim() !== '' &&
              departmentName !== '-'
                ? departmentName.trim()
                : undefined,
            departmentLink:
              departmentLink &&
              departmentLink.trim() !== '' &&
              departmentLink !== '-' &&
              departmentLink !== 'null' &&
              departmentLink !== 'undefined'
                ? departmentLink.trim()
                : undefined,
            practiceName: practice?.name,
            practiceLocation: location,
            practiceId: practice?.id,
            practiceAddress: constructPracticeAddress(practice),
          };
        })
        .filter(
          (item): item is NonNullable<typeof item> => item !== null
        );

      if (athenaDepartments.length === 0) {
        return (
          <Text fontSize="0.75rem" lineHeight="1rem" color="gray.500">
            -
          </Text>
        );
      }

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
          }}
        >
          {athenaDepartments.map((dept, index) => (
            <React.Fragment key={index}>
              {index > 0 && <Divider borderColor="gray.200" />}
              <AthenaDepartmentLink
                departmentName={dept.departmentName}
                departmentLink={dept.departmentLink}
                practiceName={dept.practiceName}
                practiceLocation={dept.practiceLocation}
                practiceId={dept.practiceId}
                practiceAddress={dept.practiceAddress}
              />
            </React.Fragment>
          ))}
        </div>
      );
    },
  },
  {
    header: 'New Patient Status',
    accessor: 'customFields',
    isCustomRender: true,
    render: (row: Provider) => {
      const newPatientValue = getCustomFieldValue(
        row.customFields,
        'New-Patient'
      );
      const newPatientProps =
        getNewPatientPriorityProps(newPatientValue);

      return newPatientProps?.fieldValue ? (
        <CustomFieldLabel
          customField="New Patient"
          fieldValue={newPatientProps?.fieldValue}
          fieldValueBgColor={newPatientProps?.fieldValueBgColor}
          fieldValueTextColor={newPatientProps?.fieldValueTextColor}
          hideCustomFieldLabel={false}
        />
      ) : null;
    },
  },
  {
    header: 'On PAC',
    accessor: 'onPac',
    isCustomRender: true,
    render: (row: Provider) => (
      <Flex width="20rem" wrap={'wrap'}>
        <ProviderPacStatus
          providerBridge={row.providerBridge}
          relatedPractices={row.relatedPractices}
        />
      </Flex>
    ),
  },
  {
    header: 'Treats Ages',
    accessor: 'customFields',
    isCustomRender: true,
    render: (row: Provider) =>
      row.customFields?.['Age'] ? (
        <Text>
          {row.customFields['Age']
            .map((field) => field.value)
            .join(', ')}
        </Text>
      ) : (
        <Text>All ages treated</Text>
      ),
  },
  {
    header: 'Supervisor/Supervisees',
    accessor: 'customFields',
    isCustomRender: true,
    render: (row: Provider) => (
      <SupervisingPhysicianBadge
        customField={row.customFields?.['Supervision-Physician']}
      />
    ),
  },
];

// Backward compatibility export - uses empty duplicateProviders
export const providerColumns: Column<Provider>[] =
  createProviderColumns({});
